import Breadcrumb from "@/components/Breadcrumb";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import MaterialSupplierTable from "./MaterialSupplierTable";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
} from "@/styles/globalstyles";
import { usePermissions } from "@/hooks/usePermissions";
import { useEffect, useMemo, useState } from "react";
import Add from "@mui/icons-material/Add";
import { useGetAllMaterialTypes } from "@/hooks/materialTypeHook";
import { useGetMaterialSuppliers } from "@/hooks/materialSupplierHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import type { MaterialSupplier } from "@/api/materialSupplierService";
import SaveMaterialSupplierModal from "@/components/material-supplier/SaveMaterialSupplierModal";
import { useSearchParams } from "react-router-dom";

const MaterialSupplierManagement = () => {
  const canEdit = usePermissions().canEdit("material.supplier");
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [filterMatTypeId, setFilterMatTypeId] = useState<number | undefined>(
    undefined,
  );
  const {
    data: matTypeData,
    isLoading: matTypeIsLoading,
    isError: matTypeIsError,
  } = useGetAllMaterialTypes({ active_only: true });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = useMemo(
    () => Number(searchParams.get("active")) || 0,
    [searchParams],
  );
  const page = useMemo(
    () => Number(searchParams.get("page")) || 0,
    [searchParams],
  );
  const rowsPerPage = useMemo(
    () => Number(searchParams.get("limit")) || 10,
    [searchParams],
  );
  const search = useMemo(() => {
    const search = searchParams.get("search") || "";
    setSearchFilter(search);
    return search;
  }, [searchParams]);
  const materialTypeId = useMemo(() => {
    if (!matTypeData) return undefined;
    const materialTypeId = searchParams.get("material_type_id")
      ? Number(searchParams.get("material_type_id"))
      : undefined;
    setFilterMatTypeId(materialTypeId);
    return materialTypeId;
  }, [searchParams, matTypeData]);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false);
  const [selectedMaterialSupplier, setSelectedMaterialSupplier] =
    useState<MaterialSupplier | null>(null);

  const {
    data: matSuppliersData,
    isLoading: matSuppliersIsLoading,
    isError: matSuppliersIsError,
  } = useGetMaterialSuppliers({
    search,
    material_type_id: materialTypeId || undefined,
    skip: page * rowsPerPage,
    limit: rowsPerPage,
    active_only: tabValue === 0,
  });

  useQueryHandler({
    isError: matTypeIsError || matSuppliersIsError,
    errorMessage: "เกิดข้อผิดพลาดในการโหลดข้อมูล",
  });

  useEffect(() => {
    document.title = "Material Supplier | AMPA";

    const savedParams = sessionStorage.getItem(`materialSupplier-params`);
    if (savedParams && searchParams.toString() === '') {
      const params = JSON.parse(savedParams);
      setSearchParams(params, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    console.log("searchParams changed:", searchParams.toString());
    if (searchParams.toString()) {
      sessionStorage.setItem(
        `materialSupplier-params`,
        JSON.stringify(Object.fromEntries(searchParams)),
      );
    }
  }, [searchParams]);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("active", String(newValue));
      newParams.set("page", String(0));
      return newParams;
    }, { replace: true });
  };

  const handleSearch = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (searchFilter) {
        newParams.set("search", searchFilter);
      } else {
        newParams.delete("search");
      }
      if (filterMatTypeId) {
        newParams.set("material_type_id", String(filterMatTypeId));
      } else {
        newParams.delete("material_type_id");
      }
      newParams.set("page", String(0));
      return newParams;
    }, { replace: true });
  };

  const handleClickAddMaterialSupplier = () => {
    setSelectedMaterialSupplier(null);
    setIsSaveModalOpen(true);
  };

  const handleClickEditMaterialSupplier = (data: MaterialSupplier) => {
    setSelectedMaterialSupplier(data);
    setIsSaveModalOpen(true);
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb current="material.supplier" />
      <Box mt={1}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              label="ค้นหา Supplier name หรือ code"
              variant="outlined"
              size="small"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              sx={{
                mt: 2,
                mb: 1,
                width: "30%",
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#cccccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#999999",
                  },
                },
              }}
            />
            <FormControl
              size="small"
              sx={{ mt: 2, mb: 1, mx: 1, width: "30%" }}
            >
              <InputLabel
                id="mattype-filter-label"
                sx={{
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                }}
              >
                เลือก Material Type
              </InputLabel>
              <Select
                labelId="mattype-filter-label"
                value={filterMatTypeId || Number(searchParams.get("material_type_id"))}
                onChange={(e) => setFilterMatTypeId(e.target.value)}
                label="เลือก Material Type"
              >
                {matTypeIsLoading ? (
                  <MenuItem disabled>กำลังโหลด...</MenuItem>
                ) : (
                  [
                    <MenuItem key="all" value={undefined}>
                      <Typography>ทั้งหมด</Typography>
                    </MenuItem>,
                    ...(matTypeData?.map((matType) => (
                      <MenuItem key={matType.id} value={matType.id}>
                        <Typography>{matType.material_type_name}</Typography>
                      </MenuItem>
                    )) ?? []),
                  ]
                )}
              </Select>
            </FormControl>
            <Button
              sx={{ mt: 2, mb: 1, ...DefaultPrimaryButton }}
              variant="contained"
              onClick={handleSearch}
            >
              ค้นหา
            </Button>
          </Box>
          <Box gap={1} display="flex">
            <Button
              variant="outlined"
              sx={{
                ...DefaultSecondaryButton,
                mt: 2,
                mb: 1,
                alignItems: "center",
              }}
              onClick={() => handleClickAddMaterialSupplier()}
              disabled={!canEdit}
            >
              <Add /> เพิ่ม Material Supplier
            </Button>
          </Box>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleChangeTab}>
            <Tab label="Active" />
            <Tab label="Inactive" />
          </Tabs>
        </Box>
        <Box sx={{ p: 1, borderRadius: 1 }}>
          <MaterialSupplierTable
            matType={matTypeData || []}
            data={matSuppliersData?.data || []}
            isLoading={matTypeIsLoading || matSuppliersIsLoading}
            isActive={tabValue === 0}
            rowCount={matSuppliersData?.total ?? 0}
            canEdit={canEdit}
            handleEdit={(data) => () => {
              handleClickEditMaterialSupplier(data);
            }}
            page={page}
            setPage={(newPage: number) => {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("page", String(newPage));
                return newParams;
              }, { replace: true });
            }}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(newRowsPerPage: number) => {
              setSearchParams((prev) => {
                console.log("newRowsPerPage", newRowsPerPage);
                const newParams = new URLSearchParams(prev);
                newParams.set("limit", String(newRowsPerPage));
                newParams.set("page", String(0));
                return newParams;
              }, { replace: true });
            }}
          />
        </Box>
      </Box>
      <SaveMaterialSupplierModal
        open={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        materialSupplierId={selectedMaterialSupplier?.supplier_id}
        matType={matTypeData || []}
      />
    </Box>
  );
};

export default MaterialSupplierManagement;
